<template>
  <ValidationObserver v-slot="{ handleSubmit, invalid ,reset}" ref="observer">
    <artisio-modal size="lg" :visible="modal.show" :data="model"
                   :title="model.uuid ? $t(`Update Custom Lot Sequence`) : $t(`Create New Custom Lot Sequence`)"
                   @show="onShow" @hidden="onHidden" @ok.prevent="handleSubmit(onSubmit)"
                   :show-confirmation="showConfirmation" :ok-title="$t('Submit')"
                   :is-loading="loading" scrollable>

      <div class="row" v-if="itemsRenderLoading">
        <div class="col-md-12">
          <spinner show/>
        </div>
      </div>

      <div v-show="!itemsRenderLoading">
        <div class="row">
          <div class="col-md-6">
            <input-widget :model="model" attribute="title"/>
          </div>
          <div class="col-md-6">
            <input-widget :model="model" attribute="comment"/>
          </div>
        </div>

        <div class="row mb-3 mt-3">
          <div class="col-sm-6">
            <h5 class="m-0">{{ $t('Sequence Items') }}</h5>
          </div>
          <div class="col-sm-6 text-right">
            <b-button variant="link" @click="addNewSequenceItem" class="p-0" :disabled="items.length > 5">
              <font-awesome-icon :icon="'plus-circle'"/>
              {{ $t('Add New Sequence Item') }}
            </b-button>
          </div>
        </div>

        <div class="row">
          <div class="col-md-12">
            <draggable :options="{'disabled': loading}" :list="items" tag="div" handle=".handle">
              <div class="row" v-for="(item, outerIndex) in items" :key="`item-row-${item.uuid}`">

                <div class="col-md-4 d-flex">
                  <div class="d-flex justify-content-between mr-2" style="width: 4em">
                    <font-awesome-icon class="icon handle hover-row-resize mr-2 mt-2" icon="bars"/>
                    <b-form-input :value="outerIndex + 1" disabled></b-form-input>
                  </div>

                  <input-widget :model="item" attribute="type" value-field="value" display-field="${item.key}"
                                @select2Select="onTypeChange(item)"
                                type="select2" :items="itemTypes" :label="false" :options="{placeholder: $t('Type')}"
                                style="flex: 1"/>
                </div>

                <div class="col-md-3" v-if="item.type === 'dynamic_field'">
                  <input-widget :model="item" attribute="dynamic_field_key" value-field="key"
                                @select2Select="onDynamicFieldSelect($event, item)"
                                @select2Unselect="onDynamicFieldUnselect(item)"
                                display-field="${item.label}" :options="{placeholder: $t('Field')}"
                                type="select2" :items="uniqueDynamicFieldsArr" :label="false"/>
                </div>

                <div class="col-md-3">
                  <input-widget :model="item" attribute="sorting" value-field="value" display-field="${item.key}"
                                type="select2"
                                :items="
                                ['department', 'category'].includes(item.type) ||
                                (item.type === 'dynamic_field' && item.dynamic_field && simpleFieldClassNames.includes(item.dynamic_field.field_class_name)) ?
                                [...sortingOptions, {key: $t('Custom'), value: 'custom'}] :
                                [...sortingOptions]
                                "
                                :label="false"
                                :options="{placeholder: $t('Sorting')}" @select2Select="onSortTypeChange(item)"/>
                </div>

                <div class="col-md-2">
                  <b-button variant="primary" size="sm" @click="onAddCustomOptionClick(item)" class="mt-1 mr-2"
                            v-if="
                          item.sorting === 'custom' && (
                              ['department', 'category'].includes(item.type) ||
                              (item.type === 'dynamic_field' && item.dynamic_field && simpleFieldClassNames.includes(item.dynamic_field.field_class_name))
                            )
                          "
                            v-b-tooltip.hover.top="$t('Add Custom Option')">
                    <font-awesome-icon icon="plus" class="icon"/>
                  </b-button>
                  <b-button variant="danger" size="sm" @click="onRemoveItemClick(item)" class="mt-1"
                            v-b-tooltip.hover.top="$t('Remove')">
                    <font-awesome-icon icon="times" class="icon"/>
                  </b-button>
                </div>

                <div class="col-md-12" v-if="item.sorting === 'custom'">
                  <template v-if="item.type === 'department' || item.type === 'category'">
                    <draggable :options="{'disabled': loading}"
                               :list="item.items" tag="div" handle=".handleInner">
                      <div class="row" v-for="(customOptionItem, innerIndex) in item.items"
                           :key="`custom-item-row-${customOptionItem.uuid}`">
                        <div class="col-md-6 d-flex justify-content-start pl-5">
                          <div class="d-flex justify-content-between mr-2" style="width: 5.5em">
                            <font-awesome-icon class="icon handleInner hover-row-resize mr-2 mt-2" icon="bars"/>
                            <b-form-input class="text-center" :value="`${outerIndex + 1}.${innerIndex+1}`"
                                          disabled></b-form-input>
                          </div>
                          <department-select-input-widget :model="customOptionItem" attribute="department_uuid"
                                                          v-if="item.type === 'department'"
                                                          :label="false" :options="{placeholder: $t('Department')}"
                                                          style="width: 18em"/>
                          <category-select-input-widget :model="customOptionItem" attribute="category_uuid"
                                                        v-else-if="item.type === 'category'" with-all-categories
                                                        :label="false" :options="{placeholder: $t('Category')}"
                                                        style="width: 18em" show-department-in-name/>
                        </div>
                        <div class="col-md-1">
                          <b-button variant="danger" size="sm"
                                    @click="onRemoveCustomOptionClick(item, customOptionItem.uuid)"
                                    class="mt-1"
                                    v-b-tooltip.hover.top="$t('Remove')">
                            <font-awesome-icon icon="times" class="icon"/>
                          </b-button>
                        </div>
                      </div>
                    </draggable>
                  </template>
                  <template
                    v-else-if="item.type === 'dynamic_field' && item.dynamic_field && simpleFieldClassNames.includes(item.dynamic_field.field_class_name)">
                    <draggable :options="{'disabled': loading}"
                               :list="item.items" tag="div" handle=".handleInner2">
                      <div class="row" v-for="(customOptionItem, innerIndex) in item.items"
                           :key="`custom-item-row-${customOptionItem.uuid}`">
                        <div class="col-md-6 d-flex justify-content-start pl-5">
                          <div class="d-flex justify-content-between mr-2" style="width: 5.5em">
                            <font-awesome-icon class="icon handleInner2 hover-row-resize mr-2 mt-2" icon="bars"/>
                            <b-form-input class="text-center" :value="`${outerIndex + 1}.${innerIndex+1}`"
                                          disabled></b-form-input>
                          </div>
                          <input-widget :model="customOptionItem" attribute="dropdown_option_value" value-field="value"
                                        display-field="${item.key}" :options="{placeholder: $t('Option')}"
                                        type="select2" :items="item.dynamic_field.choices" :label="false"
                                        style="width: 18em"/>
                        </div>
                        <div class="col-md-1">
                          <b-button variant="danger" size="sm"
                                    @click="onRemoveCustomOptionClick(item, customOptionItem.uuid)"
                                    class="mt-1"
                                    v-b-tooltip.hover.top="$t('Remove')">
                            <font-awesome-icon icon="times" class="icon"/>
                          </b-button>
                        </div>
                      </div>
                    </draggable>
                  </template>
                </div>

                <div class="col-md-12">
                  <hr/>
                </div>

              </div>
            </draggable>
          </div>
        </div>
      </div>

    </artisio-modal>
  </ValidationObserver>
</template>

<script>
  import {createNamespacedHelpers} from "vuex";
  import ArtisioModal from "../../../core/components/artisio-modal/ArtisioModal";
  import ClsModel from "@/modules/setup/cls/ClsModel.model";
  import InputWidget from "@/core/components/input-widget/InputWidget";
  import draggable from "vuedraggable";
  import SequenceItemModel from "@/modules/setup/cls/SequenceItemModel";
  import {uuid} from 'vue-uuid';
  import SequenceItemCustomOptionModel from "@/modules/setup/cls/SequenceItemCustomOptionModel";
  import DepartmentSelectInputWidget from "@/core/widgets/DepartmentSelectInputWidget";
  import CategorySelectInputWidget from "@/core/widgets/CategorySelectInputWidget";
  import cloneDeep from "lodash/cloneDeep";
  import DynamicFieldModel from "@/core/components/input-widget/DynamicFieldModel";
  import i18nHelper from "@/core/services/i18n.service";
  import Spinner from "@/core/components/common/Spinner";

  const {mapState, mapActions} = createNamespacedHelpers('cls');
  const {mapState: globalMapState} = createNamespacedHelpers('global');


  export default {
    name: "ClsModal",
    components: {
      Spinner,
      CategorySelectInputWidget,
      DepartmentSelectInputWidget,
      InputWidget,
      ArtisioModal,
      draggable
    },
    data() {
      return {
        loading: false,
        showConfirmation: true,
        model: new ClsModel(),
        items: [],
        itemTypes: [
          {key: this.$i18n.t('Department'), value: 'department'},
          {key: this.$i18n.t('Category'), value: 'category'},
          {key: this.$i18n.t('Dynamic Field'), value: 'dynamic_field'},
        ],
        sortingOptions: [
          {key: this.$i18n.t('Ascending'), value: 'asc'},
          {key: this.$i18n.t('Descending'), value: 'desc'},
        ],
        simpleFieldClassNames: [
          'AppendableChoiceField',
          'MultipleChoiceField',
          'ChoiceField',
        ],
      }
    },
    computed: {
      ...mapState({
        modal: state => state.modal,
        editObject: state => state.modal.object,
        itemsRenderLoading: state => state.modal.itemsRenderLoading,
      }),
      ...globalMapState({
        uniqueDynamicFields: state => state.uniqueDynamicFields,
      }),
      uniqueDynamicFieldsArr() {
        const returnArr = []
        for (let dfKey in this.uniqueDynamicFields) {
          returnArr.push({
            ...this.uniqueDynamicFields[dfKey],
            key: dfKey
          })
        }
        returnArr.sort((a, b) => a.label.localeCompare(b.label))
        return returnArr
      }
    },
    watch: {
      editObject() {
        if (this.editObject && this.editObject.uuid) {
          this.items = []
          let tmpEditObject = cloneDeep(this.editObject)
          let tmpItems = tmpEditObject.data
          delete tmpEditObject.data
          this.model = new ClsModel(tmpEditObject);

          let tmpAssignItems = []

          if (tmpItems && tmpItems.length > 0) {
            for (let item of tmpItems) {
              let dynamic_field = null
              let innerItems = []
              if (item.customSortingOptions && item.customSortingOptions.length > 0) {
                for (let innerItem of item.customSortingOptions) {
                  innerItems.push(new SequenceItemCustomOptionModel({
                    department_uuid: innerItem.department_uuid,
                    category_uuid: innerItem.category_uuid,
                    dropdown_option_value: innerItem.dropdown_option_value,
                    uuid: innerItem.uuid
                  }))
                }
              }

              if (item.type === 'dynamic_field') {
                let tmpDf = this.uniqueDynamicFieldsArr.find(df => df.key === item.dynamic_field_key)
                if (tmpDf) {
                  dynamic_field = cloneDeep(tmpDf)
                  this.initChoices(tmpDf, dynamic_field)
                }
              }

              let tmpModel = new SequenceItemModel({
                type: item.type,
                sorting: item.sorting,
                dynamic_field_key: item.dynamic_field_key,
                uuid: item.uuid,
                items: innerItems,
                dynamic_field: dynamic_field
              })

              tmpAssignItems.push(tmpModel)
            }
          }

          this.items = tmpAssignItems

          setTimeout(() => {
            this.updateItemsRenderLoading(false)
          }, 500)
        } else if (this.editObject && this.editObject.items && this.editObject.items.length > 0) {
          this.addNewSequenceItem()
        }
      },
    },
    methods: {
      ...mapActions(['hideModal', 'createCustomLotSequence', 'updateCustomLotSequence', 'updateItemsRenderLoading']),
      onShow() {
        this.showConfirmation = true
        this.model = new ClsModel();
      },
      onHidden() {
        this.model = new ClsModel();
        this.items = []
        this.hideModal();
      },
      async onSubmit() {
        if (this.items.length === 0) {
          this.$errorDialog(this.$i18n.t('You cannot create custom lot sequence without sequence items. Please, add at least 1 sequence item and try again.'))
          return
        }

        let requestItems = []

        for (let item of this.items) {
          const isDf = item.type === 'dynamic_field'
          const isDept = item.type === 'department'
          const isCat = item.type === 'category'

          let myObj = {
            type: item.type,
            sorting: item.sorting,
            customSortingOptions: [],
            dynamic_field_key: item.dynamic_field_key,
            uuid: item.uuid
          }

          if (item.items && item.items.length > 0 && item.sorting === 'custom') {
            if (isDept || isCat || isDf) {
              for (let innerItem of item.items) {
                myObj.customSortingOptions.push({
                  department_uuid: innerItem.department_uuid,
                  category_uuid: innerItem.category_uuid,
                  dropdown_option_value: innerItem.dropdown_option_value,
                  uuid: innerItem.uuid
                })
              }
            }
          }

          requestItems.push(myObj)
        }

        this.showConfirmation = false
        let response;
        let successMessage;
        let requestData = this.model.toJSON()
        requestData.data = requestItems

        if (!this.model.uuid) {
          response = await this.createCustomLotSequence(requestData)
          successMessage = this.$t('Custom lot sequence was successfully created')
        } else {
          response = await this.updateCustomLotSequence(requestData)
          successMessage = this.$t('Custom lot sequence was successfully update')
        }

        if (response.success) {
          this.$toast('success', successMessage);
          this.hideModal()
        } else {
          this.showConfirmation = true
          if (response.toastError) {
            this.$errorDialog(response.toastError);
          } else if (response.body.non_field_errors) {
            this.$errorDialog(response.body.non_field_errors[0]);
          } else {
            this.model.setMultipleErrors(response.body);
          }
        }
      },
      addNewSequenceItem() {
        this.items.push(new SequenceItemModel({
          uuid: uuid.v4(),
          sorting: 'asc'
        }))
      },
      onRemoveItemClick(item) {
        let index = this.items.findIndex(i => i.uuid === item.uuid)
        if (index > -1) {
          this.items.splice(index, 1)
        }
      },
      onRemoveCustomOptionClick(mainItem, customOptionUuid) {
        let index = mainItem.items.findIndex(i => i.uuid === customOptionUuid)
        if (index > -1) {
          mainItem.items.splice(index, 1)
        }
      },
      onAddCustomOptionClick(mainItem) {
        mainItem.items.push(new SequenceItemCustomOptionModel({
          uuid: uuid.v4(),
        }))
      },
      onDynamicFieldSelect(selectedItem, item) {
        for (let tmpItem of this.items) {
          if (tmpItem.type === 'dynamic_field' && tmpItem.uuid !== item.uuid && tmpItem.dynamic_field_key === item.dynamic_field_key) {
            this.$errorDialog(this.$i18n.t('You cannot select same dynamic field more than once'))
            this.$nextTick(() => {
              item.dynamic_field_key = null
            })
            return false
          }
        }

        item.dynamic_field = cloneDeep(selectedItem)

        if (selectedItem.type === 'select2' && this.simpleFieldClassNames.includes(selectedItem.field_class_name)) {
          this.initChoices(selectedItem, item.dynamic_field)
        }
      },
      onDynamicFieldUnselect(selectedItem, item) {
        item.dynamic_field = null
      },
      onTypeChange(item) {
        item.items = []
        item.dynamic_field_key = null
        item.dynamic_field = null

        if (['category', 'department'].includes(item.type)) {
          for (let tmpItem of this.items) {
            if (tmpItem.uuid !== item.uuid && item.type === tmpItem.type) {
              this.$errorDialog(this.$i18n.t('You cannot add {type} type more than once', {type: item.type}))
              this.$nextTick(() => {
                item.type = null
              })
            }
          }
        }

      },
      onSortTypeChange(item) {
        this.$nextTick(() => {
          if (item.sorting === 'custom') {
            if (item.items && item.items.length === 0) {
              if (item.type === 'dynamic_field' && item.dynamic_field && this.simpleFieldClassNames.includes(item.dynamic_field.field_class_name)) {
                this.onAddCustomOptionClick(item)
              } else {
                this.onAddCustomOptionClick(item)
              }
            }
          }
        })
      },
      initChoices(selectedItem, assignItem) {
        let tmpModel = new DynamicFieldModel(selectedItem)
        assignItem.choices = tmpModel.getChoices(i18nHelper.getMasterLanguage().key)
      }
    },
  }
</script>


<style lang="scss" scoped>
  @import "./scss/variables";

</style>
